// Diagram helpers: the frame, the deck defaults, and the cache revision (TODO §21/§3, §9).
//
// Pure and engine-agnostic where it can be. The two functions that genuinely need
// Excalidraw take the loaded module as an argument rather than importing it, so this file
// stays outside the lazy chunk boundary `diagram-engine.ts` draws.

import type { DiagramEngine } from "@/lib/diagram-engine";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import type { DiagramDocument } from "@/types/diagram";
import type { GriddStyleSheetData } from "@/types/document";

// §3.1 The frame element that IS the slide's page. Named so it is recognisable in
// Excalidraw's own frame list if the author ever opens it.
export const DIAGRAM_FRAME_NAME = "Slide";

// The frame's authored size when a window's own logical size cannot be measured (a window
// that has not laid out yet). 16:9-ish and generous: §3.4 letterboxes rather than reflows,
// so a frame that is a little large is fitted down, never cropped.
export const DEFAULT_FRAME_W = 1200;
export const DEFAULT_FRAME_H = 675;

// Excalidraw's frames are positioned in scene coordinates; the page starts at the origin so
// a fresh diagram opens with the frame where `zoomToFitBounds` will put the viewport anyway,
// and so scratch space (§3.5) is the whole plane around it.
export const FRAME_ORIGIN = { x: 0, y: 0 };

export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// A minimal structural read of an Excalidraw element. `DiagramDocument.elements` is
// `unknown[]` on purpose (§4.2.3) — the element shape is Excalidraw's to define — so the few
// fields we genuinely need are narrowed here, once, rather than by casting at each call.
interface ElementLike {
  id?: unknown;
  type?: unknown;
  x?: unknown;
  y?: unknown;
  width?: unknown;
  height?: unknown;
}

function asElement(value: unknown): ElementLike | null {
  return value && typeof value === "object" ? (value as ElementLike) : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

// §3.1/§3.2 The doc's page frame, resolved from the scene.
//
// **`frameId` is a hint, not the truth — the scene is.** It is looked up first, and if it
// resolves to nothing usable the first `type: "frame"` element wins instead. That fallback is
// not defensive padding: a scene round-trips through Excalidraw's `restore()` on every load
// and through `serializeAsJSON` on every save, and an id we hold that the scene no longer
// agrees with would silently turn the page into "no page" — which renders the whole canvas,
// scratch margin included, and is the one failure §3.5 exists to prevent. Callers that heal
// the pointer (the editor's autosave) read `id` off the result.
//
// Genuinely frameless is still a real state — the author can delete a frame like any other
// element — and every render path copes: `exportToSvg` with no `exportingFrame` clips to the
// element bounding box instead. The right panel's "Fit frame to window" is how one is
// restored.
export interface ResolvedFrame {
  id: string;
  element: unknown;
  rect: FrameRect;
}

export function resolveFrame(doc: DiagramDocument): ResolvedFrame | null {
  const read = (raw: unknown): ResolvedFrame | null => {
    const el = asElement(raw);
    if (!el || typeof el.id !== "string") return null;
    const x = num(el.x);
    const y = num(el.y);
    const width = num(el.width);
    const height = num(el.height);
    if (x === null || y === null || width === null || height === null)
      return null;
    if (width <= 0 || height <= 0) return null;
    return { id: el.id, element: raw, rect: { x, y, width, height } };
  };

  if (doc.frameId) {
    const named = doc.elements.find(
      (raw) => asElement(raw)?.id === doc.frameId,
    );
    const resolved = named ? read(named) : null;
    if (resolved) return resolved;
  }
  for (const raw of doc.elements) {
    if (asElement(raw)?.type !== "frame") continue;
    const resolved = read(raw);
    if (resolved) return resolved;
  }
  return null;
}

export function frameRect(doc: DiagramDocument): FrameRect | null {
  return resolveFrame(doc)?.rect ?? null;
}

// The raw frame element, for handing to `exportToSvg({ exportingFrame })`, which wants the
// element and not a rectangle.
export function frameElement(doc: DiagramDocument): unknown | null {
  return resolveFrame(doc)?.element ?? null;
}

// === The cache revision (§5.2) ===
//
// Every render path keys the cached SVG on `docId:rev`. `rev` is a per-object token rather
// than a content hash: Redux replaces the whole `DiagramDocument` on every edit (the
// reducer assigns a new object), so object identity changes exactly when content does, and
// a WeakMap gets us a stable number for free without walking a scene of a thousand elements
// on every render. The same trick the thumbnail capturer already uses for grid snapshots.
//
// In-memory only, so tokens restart on reload — which is correct: so does the cache.
const revTokens = new WeakMap<DiagramDocument, number>();
let revSeq = 0;

export function revOf(doc: DiagramDocument): number {
  let token = revTokens.get(doc);
  if (token === undefined) {
    token = ++revSeq;
    revTokens.set(doc, token);
  }
  return token;
}

export function cacheKey(docId: string, doc: DiagramDocument): string {
  return `${docId}:${revOf(doc)}`;
}

// === Deck defaults (§9.4) ===

// The deck defaults come in **two records, and they are split for a reason that is not
// obvious and costs §9.4 outright if it is missed.**
//
// `serializeAsJSON(…, "local")` does not persist `appState`; it persists a CURATED FOUR keys
// of it. From the package's own `APP_STATE_STORAGE_CONF`, the entries marked `export: true`
// are exactly `gridSize`, `gridStep`, `gridModeEnabled` and `viewBackgroundColor`. Every
// `currentItem*` default — roughness, font family, stroke colour, the whole of §9.4 and
// §9.2 — is dropped, and `objectsSnapModeEnabled` with them.
//
// That is correct of Excalidraw (those are per-session tool settings, which its own app keeps
// in localStorage) and fatal to us: §4.2.1 has us write through `serializeAsJSON` on every
// autosave, so seeding them into `appState` means **they survive until the first edit and no
// longer**. Reopen the diagram and every new shape is hand-drawn again.
//
// So: `appState` holds what Excalidraw persists, and `DiagramDocument.defaults` holds what we
// do. The editor merges ours over theirs when it mounts. This does not reopen §4.2.2 — that
// rule is against persisting the LIVE appState, and this is a small record we author
// ourselves and never read back off the canvas.

// §9.2/§9.4's item defaults — OURS. Re-applied on every mount, which also means a diagram's
// tool defaults return to the deck's on reopen rather than to whatever the author last
// happened to pick. That is the behaviour §9.2 asks for: the sheet drives the defaults
// OFFERED, and it never rewrites elements already on the canvas (§9.1).
export function seedItemDefaults(
  engine: DiagramEngine,
  styleSheet: GriddStyleSheetData,
): Record<string, unknown> {
  const swatches = sheetSwatches(styleSheet);
  const byName = (name: string): string | undefined =>
    swatches.find((c) => c.name === name)?.value;
  return {
    // §9.4 Architect: no sketchiness, square corners. The package exports `FONT_FAMILY` and
    // `ROUNDNESS` but not `ROUGHNESS`, so the value is written out — 0 is `architect`, 1
    // `artist`, 2 `cartoonist`, and the panel's Sloppiness control (§8.4) offers all three.
    currentItemRoughness: 0,
    currentItemRoundness: "sharp",
    // Nunito is the package's "normal" (non-hand-drawn) family. §9.5: the font is an ENUM,
    // not the sheet's `fontFamilies` list — the two do not map and pretending they do gives
    // text that is right in the editor and wrong in the export.
    currentItemFontFamily: engine.FONT_FAMILY.Nunito,
    currentItemStrokeColor: byName("text") ?? "#1e1e1e",
    currentItemBackgroundColor: "transparent",
    currentItemFillStyle: "solid",
    // Snapping on, grid off: what people expect from a box-and-arrow tool. Snapping is here
    // rather than below because it is one of the keys the package does not persist.
    objectsSnapModeEnabled: true,
  };
}

// §26 THE FOUR KEYS, named once. `seedCanvasState` seeds them and the editor's autosave guard
// watches them — and it did not, which is how the canvas background came to be un-saveable:
// the guard asked "did the elements or the files move?", a background change moves neither, so
// the write was skipped every time and the colour lived only until the session ended. A guard
// over what is PERSISTED has to know the whole of what is persisted, so both sides read this.
export const PERSISTED_APP_STATE_KEYS = [
  "viewBackgroundColor",
  "gridModeEnabled",
  "gridSize",
  "gridStep",
] as const;

// The persisted canvas keys as one comparable string, for that guard.
export function canvasStateSignature(appState: unknown): string {
  const state = (appState ?? {}) as Record<string, unknown>;
  return PERSISTED_APP_STATE_KEYS.map((key) => String(state[key])).join("|");
}

// The canvas state Excalidraw itself persists. Only these four keys survive a round trip, so
// only these four are worth seeding here — and all four are edited by §8.4's Diagram group,
// which is why they belong in the saved record rather than in the defaults above.
export function seedCanvasState(
  styleSheet: GriddStyleSheetData,
): Record<string, unknown> {
  void styleSheet; // the sheet has no canvas-background concept; kept for symmetry
  return {
    viewBackgroundColor: "#ffffff",
    gridModeEnabled: false,
  };
}

// === Creating a diagram (§3.1) ===

// One frame sized to the window's logical rect, and nothing else.
//
// Built with `convertToExcalidrawElements` rather than by hand — a frame is a real element
// with a version, a seed and a fractional index, and hand-rolling one is the "hand-picking
// fields" trap §4.2.1 exists to close — and then **its position is corrected**, because that
// function does not honour an origin of `0`. Its frame pass reads:
//
//     const frameX = frame?.x || minX;   // data/transform.ts
//
// `0` is falsy, so a frame asked for at the origin is silently moved to the bounding box of
// its `children` — and for a childless frame `getCommonBounds([])` returns `[0,0,0,0]`, which
// the same pass then pads by 10, landing the page at **(-10, -10)** instead of (0, 0). Small,
// invisible, and exactly the kind of drift §3 exists to stop: the frame is what "the slide's
// available space" IS, so it has to be where we put it. `newElementWith` is the public,
// non-mutating way to say so, and it keeps everything the constructor filled in.
//
// The same `||` would drop a width or height of 0, which cannot happen here — both are
// clamped to a positive default above.
export function createDiagramDoc(
  engine: DiagramEngine,
  size: { width: number; height: number },
  styleSheet: GriddStyleSheetData,
): DiagramDocument {
  const width = Math.round(size.width > 0 ? size.width : DEFAULT_FRAME_W);
  const height = Math.round(size.height > 0 ? size.height : DEFAULT_FRAME_H);
  const built = engine.convertToExcalidrawElements([
    {
      type: "frame",
      children: [],
      name: DIAGRAM_FRAME_NAME,
      x: FRAME_ORIGIN.x,
      y: FRAME_ORIGIN.y,
      width,
      height,
    },
  ]);
  const frame = built.find((el) => el.type === "frame");
  const elements = frame
    ? built.map((el) =>
        el.id === frame.id
          ? engine.newElementWith(el, {
              x: FRAME_ORIGIN.x,
              y: FRAME_ORIGIN.y,
              width,
              height,
            })
          : el,
      )
    : built;
  return {
    version: 1,
    engine: "excalidraw",
    elements: elements as unknown[],
    frameId: frame?.id ?? null,
    appState: seedCanvasState(styleSheet),
    defaults: seedItemDefaults(engine, styleSheet),
    fileIds: [],
  };
}

// §3.4 "Fit frame to window" — an EXPLICIT, undoable action, never an automatic reaction to
// a layout change. Returns the frame's new size; the caller mutates through
// `updateScene`/`newElementWith` so it lands in the undo history as one step (§8.3).
// Silently resizing the frame on every layout change re-crops a drawing the author
// composed, with no undo affordance, which is why this is a button.
export function fittedFrameSize(size: { width: number; height: number }): {
  width: number;
  height: number;
} {
  return {
    width: Math.round(size.width > 0 ? size.width : DEFAULT_FRAME_W),
    height: Math.round(size.height > 0 ? size.height : DEFAULT_FRAME_H),
  };
}
